import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {ref, onMounted, watch, onUnmounted} from "vue";
import {storeToRefs} from "pinia";
import * as THREE from "three";
import {STLLoader} from "three/examples/jsm/loaders/STLLoader";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls.js";
import {cfgCommon, WarnCautionColor} from "./ctxConfig"; //  초기 설정 정보
import CtxHelpers from "./ctxHelpers.vue"; // 뷰어내 그리드, 좌표축 관련 뷰
import CtxThickGap2Mesh from "./ctxThickGap2Mesh"; // ThickGap 처리
import {getMeshDimension, setMesh2OriginUpper, getNewCamPosToMesh, moveSceneToBottom, clearScene, clearThree} from "./ctxUtils";
import {CommonStore} from "../../../store/common-store";
import {ModelStore} from "../../../store/model-store";
import {OptionsStore} from "../../../store/options-store";
import {toast} from "vue3-toastify";
import tippy from "tippy.js";


import {defineComponent} from "vue";

const __default__ = defineComponent({
  name: "CtxSTLViewer",
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  objPaths: {
    type: Array,
    default: Array, // Default value for objPath
  },
  wireframeColor: {
    type: String,
    default: "#00ff00", // Default value for wireframeColor
  },
  faceColor: {
    type: String,
    default: "#ff0000", // Default value for faceColor
  },
  defaultModelScale: {
    type: Number,
    default: 1, // Default value for model scale
  },
  gridHelperSize: {
    type: Number,
    default: 1, // Default value for grid helper scale
  },
  gridHelperDivision: {
    type: Number,
    default: 10, // Default value for grid helper interval
  },
  frame: {
    type: Object,
    default: () => {
      return {w: 1024, h: 720};
    },
  },
  analysis: {
    type: Object,
    default: null,
  },
  analysisRaw: {
    type: Object,
    default: null,
  },
  buildMaxSize: {
    type: Object,
    default: null,
  },
  manifold: {type: Object, default: () => [] as any},
  thickness: {type: Object, default: () => [] as any},
  gap: {type: Object, default: () => [] as any},

  thickWarn: {type: Number, default: () => 0.3},
  thickCaution: {type: Number, default: () => 0.5},
  gapWarn: {type: Number, default: () => 0.3},
  gapCaution: {type: Number, default: () => 0.5},
},
  setup(__props) {

const props = __props;

const {isDownloadModel, clipingValue, clipingMaxValue, clipingStepValue} = storeToRefs(CommonStore());

// [Props] -----------------------------------------------------------------------

// [ThreeJS ] --------------------------------------------------------------------
const container = ref<HTMLElement | null>(null);
// [PerspectiveCamera] 지정
const camera = new THREE.PerspectiveCamera(cfgCommon.camera.cameraFov, props.frame.w / props.frame.h, 0.1, 3000);
// [scene] 지정
const scene = new THREE.Scene();
// [renderer] 지정
const renderer = new THREE.WebGLRenderer({antialias: true});
renderer.setClearColor(0xffffff, 0);
let animationId = -1;
// [model]  타입 지정
const model = [];
const transaprentModel = [];
const wire = [];
const thickGroups = ref<THREE.Group | null>(null);
const gapGroups = ref<THREE.Group | null>(null);
// [OrbitControls] 지정
const controls = ref<OrbitControls | undefined>(undefined);

const clippingParams = {
  clipIntersection: false, //true,
  showHelpers: true,
  isWireframed: false, // Checkbox is checked by default wireframed mode
  xPlane: 1000, // initial X clipping plane position
  yPlane: 1000, //Infinity, // initial Y clipping plane position
  zPlane: 1000, // initial Z clipping plane position
};

//Define an array of colors and alpha values for each plane
const clippingPlanes = [
  new THREE.Plane(new THREE.Vector3(1, 0, 0), clippingParams.xPlane), // x 축 직각 , 초기 위치 (위 clippingParams)
  new THREE.Plane(new THREE.Vector3(0, -1, 0), clippingParams.yPlane), // y 축 직각 , 초기 위치 (위 clippingParams)
  new THREE.Plane(new THREE.Vector3(0, 0, -1), clippingParams.zPlane), // z 축 직각 , 초기 위치 (위 clippingParams)
];

/// Define an array of colors and alpha values for each plane
const planeColors = [
  {color: 0xff0000, alpha: 0.0}, // Red
  {color: 0x00ff00, alpha: 0.0}, // Green // 투명
  {color: 0x0000ff, alpha: 0.0}, // Blue
];

// Create a wrapper object that holds the Plane, color, and alpha
const planesWithColor = clippingPlanes.map((plane, index) => {
  const color = planeColors[index].color;
  const alpha = planeColors[index].alpha;
  return {plane, color, alpha};
});

// Assuming you are using a renderer with clippingPlanes support
// Update the existing renderer's clippingPlanes property
renderer.clippingPlanes = planesWithColor.map((item) => item.plane);

watch(
  () => CommonStore().getTextuerStatus,
  () => {
    if (!model.length) {
      return;
    }
    if (!transaprentModel.length) {
      return;
    }
    if (!wire.length) {
      return;
    }

    transaprentModel.forEach((o: THREE.Mesh, idx) => {
      o.visible = false;
      const material: THREE.MeshLambertMaterial = o.material as THREE.MeshLambertMaterial;
      material.color.set(props.faceColor);
    });

    if (0 === CommonStore().getTextuerStatus) {
      model.forEach((o: THREE.Object3D) => {
        o.visible = true;
      });
      transaprentModel.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
      wire.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
    }
    if (1 === CommonStore().getTextuerStatus) {
      model.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
      transaprentModel.forEach((o: THREE.Object3D) => {
        o.visible = true;
      });
      wire.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
    }
    if (2 === CommonStore().getTextuerStatus) {
      model.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
      transaprentModel.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
      wire.forEach((o: THREE.Object3D) => {
        o.visible = true;
      });
    }
    if (3 === CommonStore().getTextuerStatus) {
      const colors = ["#ff0000", "#ff8c00", "#ffff00", "#008000", "#0000ff", "#4b0082", "#800080", "#68dcc6", "#ff8c00", "#904359", "#6e4f3c", "#0000ff", "#83a7f3", "#d7f44e"];

      model.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
      transaprentModel.forEach((o: THREE.Mesh, idx) => {
        o.visible = true;
        const material: THREE.MeshLambertMaterial = o.material as THREE.MeshLambertMaterial;
        material.color.set(colors[idx]);
      });
      wire.forEach((o: THREE.Object3D) => {
        o.visible = false;
      });
    }
  }
);
watch(
  () => CommonStore().getClipingValue,
  () => {
    const {currentModel} = storeToRefs(ModelStore());
    const a = currentModel.value.modelSizeZ * 0.5 - 0.2;
    clippingPlanes[1].constant = CommonStore().getClipingValue - a;
  }
);
watch(
  () => CommonStore().getIsThicknessView,
  () => {
    transaprentModel.forEach((o: THREE.Mesh, idx) => {
      o.visible = false;
      const material: THREE.MeshLambertMaterial = o.material as THREE.MeshLambertMaterial;
      material.color.set(props.faceColor);
    });

    if (thickGroups.value) {
      thickGroups.value.visible = CommonStore().getIsThicknessView;
      if (thickGroups.value.visible) {
        if (model.length) {
          model.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (transaprentModel.length) {
          transaprentModel.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (wire.length) {
          wire.forEach((o: THREE.Object3D) => {
            o.visible = true;
          });
        }
        if (gapGroups.value) {
          gapGroups.value.visible = false;
        }
      } else {
        if (model.length) {
          model.forEach((o: THREE.Object3D) => {
            o.visible = true;
          });
        }
        if (transaprentModel.length) {
          transaprentModel.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (wire.length) {
          wire.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (gapGroups.value) {
          gapGroups.value.visible = false;
        }
      }
    }
  }
);
watch(
  () => CommonStore().getIsGapView,
  () => {
    if (gapGroups.value) {
      gapGroups.value.visible = CommonStore().getIsGapView;
      if (gapGroups.value.visible) {
        if (model.length) {
          model.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (transaprentModel.length) {
          transaprentModel.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (wire.length) {
          wire.forEach((o: THREE.Object3D) => {
            o.visible = true;
          });
        }
        if (thickGroups.value) {
          thickGroups.value.visible = false;
        }
      } else {
        if (model.length) {
          model.forEach((o: THREE.Object3D) => {
            o.visible = true;
          });
        }
        if (transaprentModel.length) {
          transaprentModel.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (wire.length) {
          wire.forEach((o: THREE.Object3D) => {
            o.visible = false;
          });
        }
        if (thickGroups.value) {
          thickGroups.value.visible = false;
        }
      }
    }
  }
);

//--------------------------------------------------------------
onMounted(async () => {
  if (!props.objPaths.length) {
    return;
  }

  camera.aspect = props.frame.w;
  camera.near = props.frame.h;

  // 장면 초기화
  initSecne();

  // stl - s3로부터 로드
  const geometrys: any = [];
  for (let i = 0; i < props.objPaths.length; i++) {
    geometrys.push(await stlLoader(props.objPaths[i] as string));
  }

  // 모델 초기화
  initModel(geometrys, props.wireframeColor, props.faceColor, props.defaultModelScale);

  isDownloadModel.value = true;

  // Setup OrbitControls
  setupOrbitControls();

  // Start the animation loop
  animate();

  // Add an event listener for the resize event to update camera aspect ratio
  // window.addEventListener("resize", () => {
  // const newAspect = props.frame.w / props.frame.h;
  // renderer.setSize(props.frame.w, props.frame.h);
  // camera.aspect = newAspect;
  // camera.updateProjectionMatrix();
  // });
});
onUnmounted(() => {
  clearScene(scene);
  if (model.length) {
    for (let i = 0; i < model.length; i++) {
      clearThree(model[i]);
    }
  }
  if (transaprentModel.length) {
    for (let i = 0; i < transaprentModel.length; i++) {
      clearThree(transaprentModel[i]);
    }
  }
  if (wire.length) {
    for (let i = 0; i < wire.length; i++) {
      clearThree(wire[i]);
    }
  }
  if (thickGroups.value) {
    clearThree(thickGroups.value);
  }
  if (gapGroups.value) {
    clearThree(gapGroups.value);
  }
  renderer.clear();

  cancelAnimationFrame(animationId);
});

const setupOrbitControls = () => {
  if (container.value) {
    controls.value = new OrbitControls(camera, container.value);
    controls.value.enableDamping = true;
    controls.value.dampingFactor = 0.05;
  }
};

const animate = () => {
  animationId = requestAnimationFrame(animate);
  if (controls.value) {
    controls.value.enabled = true;
    controls.value.update();
  }
  renderer.render(scene, camera);
};

const displayErrorMessage = (message: string) => {
  alert(message);
};

const initSecne = () => {
  if (!container.value) {
    return; // Container not available, exit
  }

  // Clear the container before appending the renderer
  container.value.innerHTML = "";
  container.value.appendChild(renderer.domElement);

  // Set up the renderer
  renderer.setSize(props.frame.w, props.frame.h);

  // 중복 설정으로 (CtxHelper에도 있고, 여기도 있고, --> CtxHelper로 통일 )
  const clippingHelpers = new THREE.Group();
  clippingHelpers.add(new THREE.PlaneHelper(clippingPlanes[1], 10, 0x00ff00)); // green
  clippingHelpers.visible = false;
  clippingHelpers.name = "clippingHelpers";

  // 기존에 없으면 추가
  let hasClippingHelpers = false;
  scene.children.forEach((child) => {
    if (child.name == "clippingHelpers") {
      hasClippingHelpers = true;
    }
  });
  if (!hasClippingHelpers) {
    scene.add(clippingHelpers);
  }

  // 최초 로딩시에는 clipping되지 않은 상태 !
  renderer.clippingPlanes = [];
  renderer.localClippingEnabled = false;

  // Add an directional light : 없는 경우에만 추가
  let hasDirectionalLight = false;
  scene.children.forEach((child) => {
    if (child instanceof THREE.DirectionalLight) {
      hasDirectionalLight = true;
    }
  });

  if (!hasDirectionalLight) {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1); // color, intensity, 0x000000: black
    directionalLight.position.set(1000, -1000, 1000);
    directionalLight.name = "directionalLight";
    scene.add(directionalLight);
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 1); // color, intensity, 0x000000: black
    directionalLight2.position.set(1000, 1000, 1000);
    directionalLight2.name = "directionalLight";
    scene.add(directionalLight2);
    const directionalLight3 = new THREE.DirectionalLight(0xffffff, 1); // color, intensity, 0x000000: black
    directionalLight3.position.set(-1000, 0, 1000);
    directionalLight3.name = "directionalLight";
    scene.add(directionalLight3);
  }
  // Light Set </>---------------------------------

  // Apply clipping planes and clipIntersection to the renderer
  renderer.clippingPlanes = clippingPlanes;
  renderer.localClippingEnabled = clippingParams.clipIntersection;
};

const stlLoader = (objPath: string) => {
  return new Promise(function (resolve, reject) {
    // Load .STL model을 로드하는 로더 지정
    const loader = new STLLoader();
    loader.load(
      objPath,
      (geometry: any) => {
        resolve(geometry);
      },
      (event: any) => {
        //
      },
      (error: any) => {
        console.error("CTX: E002An error occurred while loading the .STL model:", error);
        displayErrorMessage("CTX: E002:\nAn error loading the .STL model. \nPlease check the file path and try again.");
        reject(error);
      }
    );
  });
};

const initModel = (geometrys: [], wireframeColor: string, faceColor: string, defaultModelScale: number) => {
  const groupModels = new THREE.Group();

  for (let i = 0; i < geometrys.length; i++) {
    const geometry = geometrys[i];

    try {
      const faceMaterial = new THREE.MeshLambertMaterial({
        color: new THREE.Color(faceColor),
        side: THREE.DoubleSide,
        transparent: false, // default를 false 로 하여 dat.gui에서 사용함. //Set transparent to true//
      });
      faceMaterial.onBeforeCompile = function (shader) {
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <output_fragment>",

          `
            vec3 backfaceColor = vec3( 0.4, 0.4, 0.4 );
            gl_FragColor = ( gl_FrontFacing ) ? vec4( outgoingLight, diffuseColor.a ) : vec4( backfaceColor, opacity );
            `
        );
      };
      const mesh = new THREE.Mesh(geometry, faceMaterial);
      mesh.scale.set(defaultModelScale, defaultModelScale, defaultModelScale);

      const trasnaprentMaterial = new THREE.MeshLambertMaterial({
        color: new THREE.Color(faceColor),
        depthTest: true,
        depthWrite: true,
        side: THREE.DoubleSide,
        transparent: true, // default를 false 로 하여 dat.gui에서 사용함. //Set transparent to true//
        opacity: cfgCommon.model.opacity, // Set opacity to 0.5
      });
      const trasnaprentMesh = new THREE.Mesh(geometry, trasnaprentMaterial);

      const geo = new THREE.EdgesGeometry(geometry); // or new THREE.WireframeGeometry( geometry )
      const mat = new THREE.LineBasicMaterial({
        color: new THREE.Color(wireframeColor),
        linewidth: 1,
        transparent: true,
        opacity: 0.14,
      });
      const wireframe = new THREE.LineSegments(geo, mat as any);
      wireframe.scale.set(defaultModelScale, defaultModelScale, defaultModelScale);

      mesh.visible = true;
      trasnaprentMesh.visible = false;
      wireframe.visible = false;

      model.push(mesh);
      transaprentModel.push(trasnaprentMesh);
      wire.push(wireframe);

      groupModels.add(mesh);
      groupModels.add(trasnaprentMesh);
      groupModels.add(wireframe);

      // ---------------------------------------------------------------------------------------
      // Three.js에서 사용할 머터리얼 생성
      // const edgeMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 })
      // // Three.js에서 사용할 에지 생성
      // // const edgeGeometry = new THREE.BufferGeometry()
      // const edges = (props.manifWarn as any).flat()

      // // Float32Array의 길이 계산
      // let numRows = edges.length
      // let numCols = edges[0].length
      // let float32ArrayLength = numRows * numCols
      // let edgeIndex = 0
      // let floatIndex = 0

      // // Float32Array 생성
      // let float32Array = new Float32Array(6)

      // // 2차원 배열의 요소를 Float32Array로 복사
      // for (let i = 0; i < numRows; i++) {
      //   for (let j = 0; j < numCols; j++) {
      //     let index = i * numCols + j
      //     edgeIndex = index
      //     float32Array[floatIndex] = edges[i][j]
      //     floatIndex++
      //   }

      //   if (0 === (edgeIndex + 1) % 6) {
      //     const edgeGeometry = new THREE.BufferGeometry()
      //     edgeGeometry.setAttribute('position', new THREE.BufferAttribute(float32Array, 3))
      //     const edgeMesh = new THREE.LineSegments(edgeGeometry, edgeMaterial)
      //     groupModels.add(edgeMesh)

      //     floatIndex = 0
      //     float32Array = new Float32Array(6)
      //   }
      // }

      // const s = float32Array.slice(0, 2)

      // edgeGeometry.setAttribute(
      //   'position',
      //   new THREE.BufferAttribute(float32Array.slice(0, 6), 3)
      // )
      // const edgeMesh = new THREE.LineSegments(edgeGeometry, edgeMaterial)
      // groupModels.add(edgeMesh)

      // -----------------------------------------------------------------------------

      // [thickGap mesh load]  처리  @CtxThickGap2Mesh.ts 에 정의한 함수를 사용 함
    } catch (error) {
      console.error("CTX: E001:try..catch err: An error rendering the model:", error);
      displayErrorMessage("CTX: E001:\nAn error  rendering the model.\n Please try again.");
    }
  }

  const {currentModel} = storeToRefs(ModelStore());
  const mProcess = OptionsStore().getProcessById(currentModel.value.modelProcessId);
  if (mProcess) {
    const thickWarnDim = mProcess?.thicknessWarn;
    const thickCautionDim = mProcess?.thicknessCaution;
    const gapWarnDim = mProcess?.gapWarn;
    const gapCautionDim = mProcess?.gapCaution;
    const thickGroup = new THREE.Group();
    const gapGroup = new THREE.Group();
    const thickWarnVertices = [] as number[][];
    const thickCautionVertices = [] as number[][];
    if (props.thickness) {
      for (let f = 0; f < props.thickness.length; f++) {
        const v = props.thickness[f].v;

        if (props.thickness[f].d < thickWarnDim) {
          thickWarnVertices.push(v.a);
          thickWarnVertices.push(v.b);
          thickWarnVertices.push(v.c);
        } else if (props.thickness[f].d < thickCautionDim) {
          thickCautionVertices.push(v.a);
          thickCautionVertices.push(v.b);
          thickCautionVertices.push(v.c);
        }
      }
    }
    const gapWarnVertices = [] as number[][];
    const gapCautionVertices = [] as number[][];
    if (props.gap) {
      for (let f = 0; f < props.gap.length; f++) {
        const v = props.gap[f].v;

        if (props.gap[f].d < gapWarnDim) {
          gapWarnVertices.push(v.a);
          gapWarnVertices.push(v.b);
          gapWarnVertices.push(v.c);
        } else if (props.gap[f].d < gapCautionDim) {
          gapCautionVertices.push(v.a);
          gapCautionVertices.push(v.b);
          gapCautionVertices.push(v.c);
        }
      }
    }

    if (thickWarnVertices.length) {
      const thickWarn_mesh = CtxThickGap2Mesh(thickWarnVertices as any, WarnCautionColor.warn);
      thickWarn_mesh.name = "thickWarn_mesh";
      thickGroup.add(thickWarn_mesh);
      thickWarn_mesh.position.add(model[0].position);
    }
    if (thickCautionVertices.length) {
      const thickCaution_mesh = CtxThickGap2Mesh(thickCautionVertices as any, WarnCautionColor.caution);
      thickCaution_mesh.name = "thickCaution_mesh";
      thickGroup.add(thickCaution_mesh);
      thickCaution_mesh.position.add(model[0].position);
    }
    if (gapWarnVertices.length) {
      const thickCaution_mesh = CtxThickGap2Mesh(gapWarnVertices as any, WarnCautionColor.warn);
      thickCaution_mesh.name = "gapWarn_mesh";
      gapGroup.add(thickCaution_mesh);
      thickCaution_mesh.position.add(model[0].position);
    }
    if (gapCautionVertices.length) {
      const gapCaution_mesh = CtxThickGap2Mesh(gapCautionVertices as any, WarnCautionColor.caution);
      gapCaution_mesh.name = "gapCaution_mesh";
      gapGroup.add(gapCaution_mesh);
      gapCaution_mesh.position.add(model[0].position);
    }

    thickGroup.visible = false;
    gapGroup.visible = false;
    thickGroup.name = "thickGroup";
    gapGroup.name = "gapGroup";

    thickGroups.value = thickGroup;
    gapGroups.value = gapGroup;
    groupModels.add(thickGroup);
    groupModels.add(gapGroup);
  }

  scene.add(groupModels);

  // 별도 함수를 사용하여 바닥면 기준으로 이동 시킴 (setMesh2OriginUpper @CtxUtils.ts 를 사용)
  if (props.analysis[2]) {
    setMesh2OriginUpper(groupModels, props.analysisRaw[2].value, props.buildMaxSize);
  }

  const cameraPosition = getNewCamPosToMesh(groupModels, cfgCommon.camera.pos);
  camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);

  const yMax: number = getMeshDimension(groupModels, "Z"); // @CtxUtils.ts
  CommonStore().setClipingMaxValue(yMax + 1);
  CommonStore().setClipingValue(yMax + 1);

  // 0918 전체 scene을  x축 기준으로 90 도 회전 : 중요 !!!!!
  scene.rotation.x = -Math.PI / 2;

  // 뷰어화면의 scene,origin, 모델, camera 까지 화면 아래쪽으로 이동
  // 화면높이의 중앙이 기존 중심점이지만,3Dprinting에서는 -Z가 없음으로
  const moveDistance = props.analysisRaw[2].value.z * cfgCommon.scene.moveDistance; // 15 : Config.ts에 지정함
  moveSceneToBottom(scene, camera, moveDistance); // CtxUtils.ts에 정의되어 있음.
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "container",
    ref: container,
    class: "viewer-container"
  }, [
    _createVNode(CtxHelpers, {
      showHelpers: true,
      gridHelperSize: props.gridHelperSize,
      gridHelperDivision: props.gridHelperDivision,
      scene: _unref(scene)
    }, null, 8, ["gridHelperSize", "gridHelperDivision", "scene"])
  ], 512))
}
}

})